import { Link } from "react-router-dom";
import {
  Network,
  Shield,
  Server,
  ClipboardCheck,
  GraduationCap,
  Wifi,
  ArrowRight,
  Check,
} from "lucide-react";
import { SERVICES } from "@/data/site";
import ExpertiseStrip from "@/components/ExpertiseStrip";

const ICONS = { Network, Shield, Server, ClipboardCheck, GraduationCap, Wifi };

const NETWORK_IMG =
  "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwZXF1aXBtZW50JTIwY2FibGVzJTIwZ2xvd2luZ3xlbnwwfHx8fDE3ODI0MDU4ODJ8MA&ixlib=rb-4.1.0&q=85";
const CYBER_IMG =
  "https://images.unsplash.com/photo-1510915228340-29c85a43dcfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5JTIwSVQlMjBwcm9mZXNzaW9uYWwlMjB3b3JraW5nfGVufDB8fHx8MTc4MjQwNTg4Mnww&ixlib=rb-4.1.0&q=85";

const SERVICE_IMG = {
  "network-engineering": NETWORK_IMG,
  "cybersecurity-consulting": CYBER_IMG,
};

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="relative border-b border-slate-800/80 bg-[#060B15] overflow-hidden">
        <div className="absolute inset-0 bek-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 bek-radial-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="bek-eyebrow mb-5">Our Services</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-bold tracking-tight max-w-4xl">
            Engineering practices for <span className="bek-chrome">resilient, secure</span> infrastructure.
          </h1>
          <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
            Five focused disciplines, delivered as a single integrated consulting practice — each grounded in
            hands-on engineering and operational reality.
          </p>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 space-y-24">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon];
            const img = SERVICE_IMG[s.id];
            const isReversed = idx % 2 === 1;
            return (
              <article
                key={s.id}
                id={s.id}
                data-testid={`service-section-${s.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start scroll-mt-28"
              >
                <div className={`lg:col-span-5 ${isReversed ? "lg:order-2" : ""}`}>
                  <div className="font-mono-tech text-xs tracking-[0.3em] text-slate-500 uppercase">
                    0{idx + 1} / 0{SERVICES.length}
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <span className="w-12 h-12 grid place-items-center border border-slate-300/40">
                      <Icon className="w-6 h-6 text-slate-300" />
                    </span>
                    <h2 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight">
                      {s.title}
                    </h2>
                  </div>
                  <p className="mt-6 text-slate-300 leading-relaxed">{s.short}</p>

                  {img && (
                    <div className="mt-8 border border-slate-800 overflow-hidden">
                      <img
                        src={img}
                        alt={s.title}
                        className="w-full h-64 object-cover opacity-90"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>

                <div className={`lg:col-span-7 ${isReversed ? "lg:order-1" : ""}`}>
                  <div className="bg-[#0B1220] border border-slate-800 p-8 sm:p-10">
                    <div className="bek-eyebrow mb-6">Capabilities</div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                      {s.items.map((item, i) => (
                        <li
                          key={item}
                          data-testid={`service-item-${s.id}-${i}`}
                          className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed"
                        >
                          <Check className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 pt-6 border-t border-slate-800 flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-mono-tech tracking-widest uppercase">
                        Delivered on-site or remote
                      </span>
                      <Link
                        to="/contact"
                        data-testid={`service-cta-${s.id}`}
                        className="text-slate-300 hover:text-slate-200 text-sm font-medium inline-flex items-center gap-2"
                      >
                        Discuss this <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <ExpertiseStrip />

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold tracking-tight">
            Have a project, migration, or audit on the horizon?
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Tell us what you're trying to accomplish. We'll come back with practical next steps —
            not a sales pitch.
          </p>
          <Link to="/contact" data-testid="services-cta-request" className="bek-cta mt-10">
            Request Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
